/* ==========================================================================
   Buena Vita — catalog filtering and sorting

   Client-side, over the products already on the page. The section only renders
   the chips when the collection fits on one page, so this never claims to
   filter something it cannot see.

   The reflow uses FLIP (First, Last, Invert, Play): measure where every card
   is, change the DOM, measure again, then transform each card back to where it
   started and release it. Cards glide to their new positions instead of
   snapping — which is the whole point, since a grid that teleports on every
   filter click reads as a page reload.

   FLIP is skipped entirely under prefers-reduced-motion; the DOM changes are
   identical, only the tweening is dropped.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__bvCollectionInit) return;
  window.__bvCollectionInit = true;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $all(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function initSection(section) {
    if (section.dataset.bvColBound === '1') return;
    section.dataset.bvColBound = '1';

    var grid = section.querySelector('[data-bv-grid]');
    if (!grid) return;

    var items = $all('[data-bv-item]', grid);
    var chips = $all('[data-bv-filter]', section);
    var sortSel = section.querySelector('[data-bv-sort]');
    var noResults = section.querySelector('[data-bv-noresults]');
    var clearBtn = section.querySelector('[data-bv-clear]');

    var state = { filter: '*', sort: 'featured' };

    /* ------------------------------------------------------------------
       FLIP
       ------------------------------------------------------------------ */

    function measure() {
      var map = new Map();
      items.forEach(function (item) {
        if (item.hidden) return;
        map.set(item, item.getBoundingClientRect());
      });
      return map;
    }

    function play(before) {
      if (reduced) return;

      items.forEach(function (item) {
        if (item.hidden) return;

        var first = before.get(item);
        var last = item.getBoundingClientRect();

        /* A card that was hidden a moment ago has no "first" — fade it in from
           slightly below rather than sliding it from an imaginary position. */
        if (!first) {
          item.style.transition = 'none';
          item.style.opacity = '0';
          item.style.transform = 'translateY(10px) scale(.98)';
          requestAnimationFrame(function () {
            item.style.transition = 'opacity .34s ease, transform .38s cubic-bezier(.22,.61,.36,1)';
            item.style.opacity = '';
            item.style.transform = '';
          });
          return;
        }

        var dx = first.left - last.left;
        var dy = first.top - last.top;
        if (!dx && !dy) return;

        item.style.transition = 'none';
        item.style.transform = 'translate(' + dx + 'px, ' + dy + 'px)';

        requestAnimationFrame(function () {
          item.style.transition = 'transform .42s cubic-bezier(.22,.61,.36,1)';
          item.style.transform = '';
        });
      });
    }

    /* Clear the inline styles FLIP leaves behind, so hover transforms defined
       in CSS are not permanently outranked by a leftover inline transform. */
    grid.addEventListener('transitionend', function (e) {
      var item = e.target.closest ? e.target.closest('[data-bv-item]') : null;
      if (!item) return;
      item.style.transition = '';
      item.style.transform = '';
      item.style.opacity = '';
    });

    /* ------------------------------------------------------------------
       Apply
       ------------------------------------------------------------------ */

    function matches(item) {
      if (state.filter === '*') return true;
      var tags = (item.getAttribute('data-bv-tags') || '').split(/\s+/);
      return tags.indexOf(state.filter) !== -1;
    }

    function sorted() {
      var list = items.slice();

      switch (state.sort) {
        case 'price-asc':
          list.sort(function (a, b) { return num(a, 'price') - num(b, 'price'); });
          break;
        case 'price-desc':
          list.sort(function (a, b) { return num(b, 'price') - num(a, 'price'); });
          break;
        case 'title-asc':
          list.sort(function (a, b) {
            return String(a.getAttribute('data-bv-title')).localeCompare(
              String(b.getAttribute('data-bv-title')),
              undefined,
              { sensitivity: 'base' }
            );
          });
          break;
        default:
          /* "Featured" means the order Shopify handed us, which is the
             collection's own sort order set in admin — not alphabetical. */
          list.sort(function (a, b) { return num(a, 'order') - num(b, 'order'); });
      }

      return list;
    }

    function num(node, key) {
      return parseFloat(node.getAttribute('data-bv-' + key)) || 0;
    }

    function apply(animate) {
      var before = animate ? measure() : null;

      var shown = 0;
      items.forEach(function (item) {
        var keep = matches(item);
        item.hidden = !keep;
        if (keep) shown++;
      });

      /* Re-append in the new order. appendChild on an existing child moves it,
         so no removal step is needed and focus inside a card survives. */
      var order = sorted();
      order.forEach(function (item) { grid.appendChild(item); });

      /* Keep the stagger indices matching the visual order, otherwise a sorted
         grid animates in a scrambled sequence on the next page load. */
      order.forEach(function (item, i) { item.style.setProperty('--bv-i', i); });

      if (noResults) noResults.hidden = shown !== 0;
      if (shown === 0) grid.setAttribute('aria-hidden', 'true');
      else grid.removeAttribute('aria-hidden');

      if (animate) play(before);

      section.dispatchEvent(
        new CustomEvent('bv:catalog:changed', {
          bubbles: true,
          detail: { filter: state.filter, sort: state.sort, shown: shown }
        })
      );
    }

    /* ------------------------------------------------------------------
       Controls
       ------------------------------------------------------------------ */

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var next = chip.getAttribute('data-bv-filter');
        /* Clicking the active chip clears back to everything — a filter you
           cannot undo without hunting for the "all" chip is a trap. */
        state.filter = state.filter === next && next !== '*' ? '*' : next;

        chips.forEach(function (c) {
          c.setAttribute('aria-pressed', c.getAttribute('data-bv-filter') === state.filter ? 'true' : 'false');
        });

        apply(true);
        writeUrl();
      });
    });

    if (sortSel) {
      sortSel.addEventListener('change', function () {
        state.sort = sortSel.value;
        apply(true);
        writeUrl();
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        state.filter = '*';
        chips.forEach(function (c) {
          c.setAttribute('aria-pressed', c.getAttribute('data-bv-filter') === '*' ? 'true' : 'false');
        });
        apply(true);
        writeUrl();
      });
    }

    /* ------------------------------------------------------------------
       URL sync — makes a filtered view shareable and survivable across a
       back-navigation. replaceState, not pushState: a filter is not a page,
       and stacking history entries would make Back feel broken.
       ------------------------------------------------------------------ */

    function writeUrl() {
      if (!window.history || !history.replaceState) return;
      var url = new URL(window.location.href);

      if (state.filter === '*') url.searchParams.delete('goal');
      else url.searchParams.set('goal', state.filter.replace('goal:', ''));

      if (state.sort === 'featured') url.searchParams.delete('order');
      else url.searchParams.set('order', state.sort);

      history.replaceState(null, '', url.toString());
    }

    function readUrl() {
      var params = new URLSearchParams(window.location.search);

      var goal = params.get('goal');
      if (goal) {
        var wanted = 'goal:' + goal;
        if (chips.some(function (c) { return c.getAttribute('data-bv-filter') === wanted; })) {
          state.filter = wanted;
        }
      }

      var order = params.get('order');
      if (order && sortSel) {
        var valid = $all('option', sortSel).some(function (o) { return o.value === order; });
        if (valid) { state.sort = order; sortSel.value = order; }
      }

      chips.forEach(function (c) {
        c.setAttribute('aria-pressed', c.getAttribute('data-bv-filter') === state.filter ? 'true' : 'false');
      });
    }

    readUrl();
    /* No animation on first paint — the cards are still doing their reveal. */
    apply(false);
  }

  function boot(scope) {
    $all('[data-bv-collection]', scope || document).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
